/**
 * Debug Trace Logger
 *
 * Persistent debug logger that survives page reloads and hard navigations
 * (window.location.assign). Stores a ring buffer of entries in localStorage.
 *
 * Usage:
 *   import { trace } from "../utils/debugTrace";
 *   trace("Till", "guard fired", { tillSession: !!tillSession, tillAccess: typeof tillData?.tillAccess });
 *
 * To view the log:
 *   - DevTools Console: __dumpTrace()
 *   - Or: JSON.parse(localStorage.getItem("__debug_trace"))
 *
 * To clear:
 *   __clearTrace()
 *
 * Enable/disable:
 *   localStorage.setItem("__debug_enabled", "1")  — enable (DEFAULT ON for now)
 *   localStorage.setItem("__debug_enabled", "0")  — disable
 */

const STORAGE_KEY = "__debug_trace";
// BUG-2066/2067/2068: a rapid repeated-click repro produces a reboot every
// 3-4s, each one emitting ~15-20 entries (BootDiag, SingleInstanceGuard,
// CartDiag x3-5). At 200 that's only ~2 reboots of headroom — a real repro
// capture had its handleLogin/AuthRedirect sequence fully evicted by the
// later reboots' own boot noise before it could be dumped. Raised for
// headroom; still bounded, still a fixed-size ring buffer.
const MAX_ENTRIES = 1000;

// Subscriber pattern — issueRecorder taps into this
const subscribers = new Set();

export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

function isEnabled() {
  const flag = localStorage.getItem("__debug_enabled");
  // Default ON — set to "0" to disable
  return flag !== "0";
}

function getEntries() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

export function trace(source, message, data) {
  if (!isEnabled()) return;

  const entry = {
    t: new Date().toISOString().slice(11, 23), // HH:mm:ss.SSS
    src: source,
    msg: message,
  };
  if (data !== undefined) {
    entry.d = data;
  }

  // Notify subscribers (e.g. issue recorder)
  for (const fn of subscribers) {
    try { fn(entry); } catch {}
  }

  const entries = getEntries();
  entries.push(entry);
  // Ring buffer — keep last MAX_ENTRIES
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // setItem can fail for reasons other than localStorage being full -- e.g.
    // `data` contains a circular reference (a raw DOM/React event passed by
    // mistake) -- but a live BUG-2066/2067/2068 capture showed it IS quota:
    // a rapid reboot storm produced only ~10 surviving entries out of
    // hundreds actually traced, because every quota failure used to nuke
    // the whole buffer down to a single entry here, right when the incident
    // needed the preceding sequence most. Halve the buffer and retry before
    // giving up that much history — repeats on the next call if still over
    // quota, converging fast without a full wipe.
    try {
      const halved = entries.slice(-Math.max(1, Math.floor(entries.length / 2)));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(halved));
    } catch {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([entry]));
      } catch {
        try {
          const { d, ...entryWithoutData } = entry;
          localStorage.setItem(STORAGE_KEY, JSON.stringify([entryWithoutData]));
        } catch {
          // Give up silently -- logging must never break the feature that called it.
        }
      }
    }
  }

  // Also log to console for DevTools (if open)
}

// Global helpers for DevTools console
if (typeof window !== "undefined") {
  window.__dumpTrace = () => {
    const entries = getEntries();
    console.table(entries);
    return entries;
  };
  window.__clearTrace = () => {
    localStorage.removeItem(STORAGE_KEY);
  };
}
